import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/api/projects'
import { shouldNotRetryRequest } from '@/api/errors'
import { DEFAULT_QUERY_RETRY_COUNT } from '@/lib/queryClient'
import { useTenantKey } from './useTenantKey'
import type { CreateProjectRequest, UpdateProjectRequest, PaginationParams, ProjectFilters } from '@/types'

export function useProjects(pagination?: PaginationParams, filters?: ProjectFilters) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', params),
    queryFn: () => projectsApi.getAll(params),
  })
}

export function useProjectClients() {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', 'clients'),
    queryFn: projectsApi.getClients,
  })
}

export function useProject(projectId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId),
    queryFn: () => projectsApi.getById(projectId!),
    enabled: !!projectId,
    retry: (failureCount, error) =>
      !shouldNotRetryRequest(error) && failureCount < DEFAULT_QUERY_RETRY_COUNT,
  })
}

export function useProjectTeamMembers(projectId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'team-members'),
    queryFn: () => projectsApi.getTeamMembers(projectId!),
    enabled: !!projectId,
  })
}

export function useCreateProject() {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: CreateProjectRequest) => projectsApi.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects') })
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
  })
}

export function useUpdateProject() {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: UpdateProjectRequest }) =>
      projectsApi.update(projectId, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', variables.projectId, 'team-members') })
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
  })
}

export function useDeleteProject() {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (projectId: string) => projectsApi.delete(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects') })
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
  })
}

export function useDecommissionProject() {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (projectId: string) => projectsApi.decommission(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects') })
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
  })
}
